import React, { useState, useEffect } from 'react';
import { entitiesApi, ledgerApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Handshake, TrendingUp, TrendingDown, Wallet, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function PartnersPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerLedger, setPartnerLedger] = useState([]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const { data } = await entitiesApi.list('partner');
      // Get ledger data for each partner
      const enriched = await Promise.all(data.map(async (p) => {
        try {
          const { data: ledger } = await ledgerApi.get({ entity_id: p._id });
          return { ...p, invested: ledger.total_in, withdrawn: ledger.total_out, balance: ledger.balance };
        } catch {
          return { ...p, invested: 0, withdrawn: 0, balance: 0 };
        }
      }));
      setPartners(enriched);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPartners(); }, []);

  const viewLedger = async (partner) => {
    setSelectedPartner(partner);
    try {
      const { data } = await ledgerApi.get({ entity_id: partner._id });
      setPartnerLedger(data.entries);
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Partner Accounts</h1>
        <p className="text-sm text-muted-foreground mt-1">Track partner investments and withdrawals</p>
      </div>

      {partners.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center">
          <Handshake className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
          <h3 className="font-semibold mb-1">No partners yet</h3>
          <p className="text-sm text-muted-foreground">Add partners from the Entities page</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partners.map(partner => (
            <div key={partner._id} className="bg-card border border-border p-6 space-y-4" data-testid={`partner-card-${partner._id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg tracking-tight">{partner.name}</h3>
                  {partner.phone && <p className="text-sm text-muted-foreground">{partner.phone}</p>}
                </div>
                <Button variant="outline" size="sm" className="gap-1" onClick={() => viewLedger(partner)} data-testid={`view-partner-ledger-${partner._id}`}>
                  <Eye className="w-3.5 h-3.5" /> Ledger
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-3 border-t border-border">
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider"><TrendingUp className="w-3 h-3 text-emerald-600" />Invested</div>
                  <div className="font-mono text-sm font-medium text-emerald-700 dark:text-emerald-400 mt-1">{'\u20B9'}{(partner.invested || 0).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider"><TrendingDown className="w-3 h-3 text-red-600" />Withdrawn</div>
                  <div className="font-mono text-sm font-medium text-red-700 dark:text-red-400 mt-1">{'\u20B9'}{(partner.withdrawn || 0).toLocaleString('en-IN')}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground uppercase tracking-wider"><Wallet className="w-3 h-3" />Balance</div>
                  <div className={`font-mono text-sm font-medium mt-1 ${(partner.balance || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                    {'\u20B9'}{(partner.balance || 0).toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Partner Ledger Dialog */}
      <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPartner?.name} - Ledger</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto" data-testid="partner-ledger-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Type</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Money In</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Money Out</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Balance</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-muted-foreground">Notes</th>
                </tr>
              </thead>
              <tbody>
                {partnerLedger.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No entries</td></tr>
                ) : partnerLedger.map(e => (
                  <tr key={e._id} className="border-b border-border">
                    <td className="py-2 px-3">{e.date}</td>
                    <td className="py-2 px-3">
                      <span className={e.entry_type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}>
                        {e.entry_type === 'income' ? 'Money In' : 'Money Out'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-700 dark:text-emerald-400">
                      {e.entry_type === 'income' ? `\u20B9${e.amount.toLocaleString('en-IN')}` : ''}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-red-700 dark:text-red-400">
                      {e.entry_type === 'expense' ? `\u20B9${e.amount.toLocaleString('en-IN')}` : ''}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-medium ${e.running_balance >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
                      {'\u20B9'}{e.running_balance.toLocaleString('en-IN')}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground">{e.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
